<template>
  <div class="catalog-page">

    <!-- ── Intro ──────────────────────────────────────────────────────────── -->
    <section class="cat-intro">
      <div class="cat-shell">
        <p class="cat-eyebrow">ORE OFFSITE: STUDIO</p>
        <h1 class="cat-title">Catalogs</h1>
        <p class="cat-lede">
          Every ORE catalog, ready to read on your phone or download for later. Dimensions,
          finishes, and model numbers for the full pre-designed range.
        </p>
      </div>
    </section>

    <!-- ── The catalogs ───────────────────────────────────────────────────── -->
    <section class="cat-list">
      <div class="cat-shell cat-shell--wide">
        <article v-for="(c, i) in catalogs" :key="c.slug" class="cat-card">
          <a :href="c.url" target="_blank" rel="noopener" class="cat-card__cover">
            <!-- Most arrivals are a QR scan on a phone, where the first cover is the
                 hero. Load it eagerly so there is no blank card on landing. -->
            <img
              :src="c.cover"
              :alt="`Cover of the ORE ${c.nameText} catalog`"
              :loading="i === 0 ? 'eager' : 'lazy'"
              :fetchpriority="i === 0 ? 'high' : 'auto'"
              width="792"
              height="612"
            />
          </a>

          <div class="cat-card__body">
            <h2 class="cat-card__name" v-html="c.name"></h2>
            <p class="cat-card__meta">{{ c.pages }} pages &middot; PDF &middot; {{ c.size }}</p>
            <p class="cat-card__desc">{{ c.desc }}</p>

            <div class="cat-card__actions">
              <a :href="c.url" target="_blank" rel="noopener" class="cat-btn">
                View
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
              </a>
              <a :href="c.url" :download="c.file" class="cat-btn cat-btn--ghost">
                Download
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 4v11M7 11l5 5 5-5M5 20h14"/></svg>
              </a>
            </div>
          </div>
        </article>
      </div>
    </section>

    <!-- ── Back to OFFSITE ────────────────────────────────────────────────── -->
    <section class="cat-foot">
      <div class="cat-shell">
        <router-link to="/offsite" class="cat-back">&larr; ORE OFFSITE: STUDIO</router-link>
      </div>
    </section>

  </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount } from 'vue'
import coverPreDesigned from '@/assets/img/catalog/cover-pre-designed.jpg'
import coverFireFeature from '@/assets/img/catalog/cover-fire-feature.jpg'
import coverFuse from '@/assets/img/catalog/cover-fuse.jpg'

// Page counts and file sizes read from the PDFs themselves. The brand sets FuSE
// with a combining breve (U+0306), matching the main navigation.
const catalogs = [
  {
    slug: 'pre-designed',
    name: 'Pre-Designed',
    nameText: 'Pre-Designed',
    cover: coverPreDesigned,
    url: 'https://ore.design/wp-content/uploads/2026/02/ORE_Design-Catalog-Products-RTL-AR02-0626.pdf',
    file: 'ORE-Design-Pre-Designed-Catalog.pdf',
    pages: 57,
    size: '15 MB',
    desc: 'The full pre-designed range — cube, taper, column, radius, stilt, and band planters — with dimensions, finishes, and model numbers throughout.',
  },
  {
    slug: 'fire-feature',
    name: 'Fire Feature',
    nameText: 'Fire Feature',
    cover: coverFireFeature,
    url: 'https://ore.design/wp-content/uploads/2025/11/ORE_Design-Catalog-Fire_Feature-RTL-AR03-0626.pdf',
    file: 'ORE-Design-Fire-Feature-Catalog.pdf',
    pages: 29,
    size: '12 MB',
    desc: 'Square, round, cylinder, and block fire features, with base weights, alloy and patina finish options, and full specifications.',
  },
  {
    slug: 'fuse',
    name: 'Pre-Designed FŭSE<sup>&reg;</sup>',
    nameText: 'Pre-Designed FŭSE',
    cover: coverFuse,
    url: 'https://ore.design/wp-content/uploads/2026/08/ORE_Design-Catalog-FuSE-Pre_Design_Mod_System-WLS-AR01-0726.pdf',
    file: 'ORE-Design-Pre-Designed-FuSE-Catalog.pdf',
    pages: 22,
    size: '12 MB',
    desc: 'The FŭSE modular system, 7300 through 7700 series — corner, end, and middle units across every width and height, plus warranty terms.',
  },
]

onMounted(() => document.body.classList.add('page-catalog'))
onBeforeUnmount(() => document.body.classList.remove('page-catalog'))
</script>

<style lang="scss" scoped>
$white: #fff;
$orange: #ff4c00;
$green-dark: #001610;
$mist: #ebf8ef;
$font: 'FH Oscar', Helvetica, Arial, sans-serif;

.catalog-page {
  background: $mist;
  color: $green-dark;
  font-family: $font;
  min-height: 100vh;
  // Clears the fixed site header, which grows at its own 1025px breakpoint.
  padding-top: 104px;

  @media (min-width: 1025px) { padding-top: 144px; }
}

.cat-shell {
  max-width: 760px;
  margin: 0 auto;
  padding: 0 24px;

  &--wide { max-width: 1100px; }
}

// ── Intro ────────────────────────────────────────────────────────────────────
.cat-intro {
  padding: clamp(24px, 4vw, 48px) 0 clamp(28px, 4vw, 44px);
}

.cat-eyebrow {
  font-weight: 500;
  font-size: 0.8125rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: $orange;
  margin-bottom: 10px;
}

.cat-title {
  color: $green-dark;
  font-weight: 600;
  font-size: clamp(2.25rem, 7vw, 4rem);
  line-height: 1.05;
  letter-spacing: -0.01em;
  margin-bottom: 16px;
}

.cat-lede {
  color: rgba(0, 22, 16, 0.75);
  font-size: clamp(1rem, 2vw, 1.1875rem);
  line-height: 1.6;
  max-width: 46ch;
}

// ── Cards ────────────────────────────────────────────────────────────────────
.cat-list {
  padding-bottom: clamp(32px, 5vw, 56px);

  .cat-shell {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: clamp(20px, 2.6vw, 32px);

    @media (max-width: 900px)  { grid-template-columns: 1fr; max-width: 560px; }
  }
}

// The site ground is mint on <html> (#EBF8EF) and its cards are white with a 24px
// radius, no shadow, no border, image bleeding flush. Our covers are the one content
// type that breaks that idiom — they are printed ON the mint, so a white card over a
// mint page sandwiches them. No card here: the cover's own ground runs into the page
// and only the artwork reads, with the type block carrying the structure.
// Deep-green card on the site's mint ground. The covers are printed on the brand
// mint, so they need a genuinely different value behind them — $green-dark is the
// deepest colour in the palette and gives them the most contrast available without
// introducing anything off-brand. 24px radius matches the site's card idiom.
.cat-card {
  display: flex;
  flex-direction: column;
  background: $green-dark;
  border-radius: 24px;
  padding: 14px;
  transition: transform 0.3s;

  &:hover { transform: translateY(-4px); }
}

.cat-card__cover {
  display: block;
  line-height: 0;

  img {
    display: block;
    width: 100%;
    aspect-ratio: 1.294;   // native 11 x 8.5 of the catalogs
    object-fit: cover;
    border-radius: 12px;
  }

  &:focus-visible {
    outline: 2px solid $orange;
    outline-offset: 3px;
  }
}

.cat-card__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: clamp(16px, 1.8vw, 20px) 4px 4px;
}

.cat-card__name {
  color: $white;
  font-weight: 600;
  font-size: clamp(1.25rem, 2.2vw, 1.5rem);
  line-height: 1.15;
  margin-bottom: 6px;

  // The registered mark should not inflate the line box.
  :deep(sup) {
    font-size: 0.5em;
    vertical-align: super;
    line-height: 0;
  }
}

.cat-card__meta {
  font-weight: 500;
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $orange;
  margin-bottom: 12px;
}

.cat-card__desc {
  font-size: 0.9375rem;
  line-height: 1.6;
  color: rgba(235, 248, 239, 0.72);   // mint, held back so the title leads
  margin-bottom: 20px;
}

.cat-card__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: auto;   // buttons align across cards of differing copy length
}

.cat-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: $orange;
  color: $white;
  font-weight: 500;
  font-size: 0.9375rem;
  letter-spacing: 0.02em;
  // 44px minimum tap target — most of this traffic arrives by QR code on a phone.
  min-height: 44px;
  padding: 0 22px;
  border-radius: 100px;
  text-decoration: none;
  transition: opacity 0.2s, background 0.2s, color 0.2s;

  svg {
    width: 16px;
    height: 16px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:hover { opacity: 0.85; }

  &:focus-visible {
    outline: 2px solid $white;
    outline-offset: 3px;
  }

  // Outlined variant sits on the dark card, so it inverts.
  &--ghost {
    background: transparent;
    color: $white;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.42);

    &:hover {
      background: $white;
      color: $green-dark;
      opacity: 1;
    }
  }
}

// ── Foot ─────────────────────────────────────────────────────────────────────
.cat-foot {
  padding: 0 0 clamp(48px, 7vw, 88px);
}

.cat-back {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  color: $green-dark;
  font-weight: 500;
  font-size: 0.9375rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  text-decoration: underline;
  text-underline-offset: 0.2em;

  &:hover { color: $orange; }
}
</style>

<style lang="scss">
.page-catalog .main-footer {
  display: none;
}
</style>
